import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import logo from "../images/Inovaxe_logo-FINAL-3x11-45373564.png";
import ConfirmPopup from "./ConfirmPopup";
import "../Styles/Layout.css";
import config from "../Config";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { getAuthHeaders, getAuthToken } from "../Services/authService";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const username = localStorage.getItem("name") || "Admin";
  const [approvalCount, setApprovalCount] = useState(0);

  let menuPermissions = {};
  try {
    const raw = localStorage.getItem("menuPermissions");
    menuPermissions = raw ? JSON.parse(raw) : {};
    //console.log("Menu permissions:", menuPermissions);
  } catch (e) {
    console.error("Error parsing menu permissions:", e);
    menuPermissions = {};
  }

  const hasPerm = (key) => {
    const v = menuPermissions?.[key];
    if (v === true || v === 1) return true;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "true" || s === "1";
    }
    return false;
  };

  const hasAnyPerm = (keys) => keys.some((key) => hasPerm(key));

  const quotePerms = ["quotE_NEW", "quotE_SAVED", "quotE_SUBMIT", "quotE_ACTIVE", "quotE_HISTORY"];
  const salesPerms = ["saleS_NEW", "saleS_SAVED", "saleS_SUBMIT", "saleS_ACTIVE", "saleS_HISTORY"];
  const travelerPerms = ["traveL_NEW", "traveL_SAVED", "traveL_SUBMIT", "traveL_ACTIVE", "traveL_HISTORY"];
  const adminPerms = ["useR_MANAGEMENT", "partS_MANAGEMENT", "bundlE_APPROVAL", "approvaL_WORKFLOW", "customers", "menU_ACCESS"];
  const isApprovalCount =["issubmiT_APPROVAL"];
  const blackFlushReleasePerms = [
    "releasE_TO_PRODUCTION",
    "RELEASE_TO_PRODUCTION",
    "bLACK_RELEASE",
    "blacK_RELEASE",
    "BLACK_RELEASE",
    "black_release",
  ];
  const blackFlushShippedPerms = ["shipped", "SHIPPED", "bLACK_SHIPPED", "blacK_SHIPPED", "BLACK_SHIPPED", "black_shipped"];
  const userRole = (localStorage.getItem("userRole") || "").toString().toLowerCase();
  const isRestrictedUser = userRole === "user";
  const hasExplicitBackflushPerms = Object.keys(menuPermissions || {}).some((key) => {
    const normalized = key.replace(/[_-]/g, "").toLowerCase();
    return (
      normalized === "releasetoproduction" ||
      normalized === "shipped" ||
      normalized === "blackrelease" ||
      normalized === "blackshipped"
    );
  });

  const showQuoteMenu = hasAnyPerm(quotePerms);
  const showSalesMenu = hasAnyPerm(salesPerms);
  const showTravelerMenu = hasAnyPerm(travelerPerms);
  const showAdminMenu = hasAnyPerm(adminPerms);
  const showBlackFlushRelease = hasAnyPerm(blackFlushReleasePerms) || (!hasExplicitBackflushPerms && !isRestrictedUser);
  const showBlackFlushShipped = hasAnyPerm(blackFlushShippedPerms) || (!hasExplicitBackflushPerms && !isRestrictedUser);
  const showBlackFlushMenu = showBlackFlushRelease || showBlackFlushShipped;
  const showApprovalCount = hasAnyPerm(isApprovalCount);
  //console.log("Show approval count:", showApprovalCount);

  const getFirstBlackFlushPath = () => {
    if (showBlackFlushRelease) return "/release-to-production";
    if (showBlackFlushShipped) return "/shipped";
    return "/dashboard";
  };

  const getFirstAdminPath = () => {
    if (hasPerm("useR_MANAGEMENT") || hasPerm("menU_ACCESS")) return "/user-management";
    if (hasPerm("partS_MANAGEMENT")) return "/parts-management";
    if (hasPerm("bundlE_APPROVAL")) return "/bundle-management";
    if (hasPerm("approvaL_WORKFLOW")) return "/approval-workflow";
    if (hasPerm("customers")) return "/customers";
    return "/dashboard";
  };

  const [pageKey, setPageKey] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const stickyChromeRef = useRef(null);
  const [activeModule, setActiveModule] = useState(
    () => localStorage.getItem("activeModule") || "QUOTE"
  );

  // Logout
  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  // Common Navigation
  const handleNavigation = (path) => {
    navigate(path, { state: { timestamp: Date.now() } });
  };

  // NEW QUOTE POPUP
  const handleNewQuoteClick = () => {
    if (location.pathname === "/newquote") return;
    setShowConfirm(true);
  };

  const confirmNavigation = () => {
    setShowConfirm(false);
    setPageKey((prev) => prev + 1);
    navigate("/newquote", { state: { timestamp: Date.now() } });
  };

  const cancelNavigation = () => {
    setShowConfirm(false);
  };

  const fetchApprovalCount = async () => {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/Approval/ApprovalQuoteCount`,
        {
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch approval count: ${response.status}`);
      }

      const data = await response.json();
      setApprovalCount(Number(data.count || 0));
    } catch (error) {
      console.error("Error fetching approval count:", error);
    }
  };

  useEffect(() => {
    let connection = null;

    const startSignalR = async () => {
      try {
        await fetchApprovalCount();

        const apiRoot = (config.apiBaseUrl || "").replace(/\/api\/?$/, "");
        const hubUrl = `${apiRoot}/hubs/approval`;

        connection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => getAuthToken() || "",
          })
          .withAutomaticReconnect()
          .build();

        connection.on("ApprovalCountChanged", async () => {
          await fetchApprovalCount();
        });

        connection.onreconnected(() => {
          fetchApprovalCount();
        });

        await connection.start();
      } catch (error) {
        console.error("SignalR connection error:", error);
      }
    };

    startSignalR();

    return () => {
      if (connection) {
        connection.off("ApprovalCountChanged");
        connection.stop();
      }
    };
  }, []);

  // Persist module
  useEffect(() => {
    localStorage.setItem("activeModule", activeModule);
  }, [activeModule]);

  useEffect(() => {
    const el = stickyChromeRef.current;
    if (!el) return;

    const setHeight = () => {
      document.documentElement.style.setProperty(
        "--layout-sticky-height",
        `${el.offsetHeight}px`
      );
    };

    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    window.addEventListener("resize", setHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", setHeight);
    };
  }, [activeModule, showQuoteMenu, showSalesMenu, showTravelerMenu, showBlackFlushMenu, showAdminMenu]);

  useEffect(() => {
    const allowed = [];
    if (showQuoteMenu) allowed.push("QUOTE");
    if (showSalesMenu) allowed.push("SALES");
    if (showTravelerMenu) allowed.push("TRAVELER");
    if (showBlackFlushMenu) allowed.push("BLACKFLUSH");
    if (showAdminMenu) allowed.push("ADMIN");

    if (allowed.length && !allowed.includes(activeModule)) {
      setActiveModule(allowed[0]);
    }
  }, [activeModule, showQuoteMenu, showSalesMenu, showTravelerMenu, showBlackFlushMenu, showAdminMenu]);

  useEffect(() => {
    if (location.pathname === "/release-to-production" || location.pathname === "/shipped") {
      setActiveModule("BLACKFLUSH");
    }
  }, [location.pathname]);

  return (
    <div className="layout-container">
      <div className="layout-sticky-chrome" ref={stickyChromeRef}>
      <nav className="main-navbar">
        <div className="nav-left">
          <img src={logo} className="top-logo" alt="Inovaxe" />
        </div>

        <div className="nav-center">
          <div
            className="app-title clickable-title"
            onClick={() => navigate("/dashboard")}
          >
            INOVAXE QUOTING TOOL
          </div>

          <div className="module-menu">
            {showQuoteMenu && (
              <button
                className={activeModule === "QUOTE" ? "module-btn active" : "module-btn"}
                onClick={() => {
                  setActiveModule("QUOTE");
                  navigate("/dashboard");
                }}
              >
                Quote Generation
              </button>
            )}

            {showSalesMenu && (
              <button
                className={activeModule === "SALES" ? "module-btn active" : "module-btn"}
                onClick={() => {
                  setActiveModule("SALES");
                  navigate("/dashboard");
                }}
              >
                Sales Order Gen.
              </button>
            )}

            {showTravelerMenu && (
              <button
                className={activeModule === "TRAVELER" ? "module-btn active" : "module-btn"}
                onClick={() => {
                  setActiveModule("TRAVELER");
                  navigate("/dashboard");
                }}
              >
                Traveler Generation
              </button>
            )}

            {showBlackFlushMenu && (
              <button
                className={activeModule === "BLACKFLUSH" ? "module-btn active" : "module-btn"}
                onClick={() => {
                  setActiveModule("BLACKFLUSH");
                  navigate(getFirstBlackFlushPath());
                }}
              >
                Backflushing
              </button>
            )}

            {showAdminMenu && (
              <button
                className={activeModule === "ADMIN" ? "module-btn active" : "module-btn"}
                onClick={() => {
                  setActiveModule("ADMIN");
                  navigate(getFirstAdminPath());
                }}
              >
                System Admin
              </button>
            )}
          </div>
        </div>

        <div className="nav-right">
          <span className="admin-text">{username}</span>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </nav>

      {/*  SUB TABS */}
      <div className="quote-tabs">

        {/*  QUOTE / SALES / TRAVELER */}
        {showQuoteMenu && activeModule === "QUOTE" && (
          <>
            {hasPerm("quotE_NEW") && (
              <button
                className={location.pathname === "/newquote" ? "tab active" : "tab"}
                onClick={handleNewQuoteClick}
              >
                New Quote
              </button>
            )}

            {hasPerm("quotE_SAVED") && (
              <button
                className={location.pathname === "/savedquotes" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/savedquotes")}
              >
                Saved Quotes
              </button>
            )}

            {hasPerm("quotE_SUBMIT") && (
              <button
                className={location.pathname === "/approvalquotes" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/approvalquotes")}
              >
                <span>Submitted For Approval</span>
                {approvalCount > 0 && showApprovalCount === true && (
                  <span className="approval-count">{approvalCount}</span>
                )}
              </button>
            )}

            {hasPerm("quotE_ACTIVE") && (
              <button
                className={location.pathname === "/activequote" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/activequote")}
              >
                Active Quotes
              </button>
            )}

            {hasPerm("quotE_HISTORY") && (
              <button
                className={location.pathname === "/historicalquote" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/historicalquote")}
              >
                Historical Quotes
              </button>
            )}
          </>
        )}

        {showSalesMenu && activeModule === "SALES" && (
          <>
            {hasPerm("saleS_NEW") && (
              <button
                className={location.pathname === "/newquote" ? "tab active" : "tab"}
                onClick={handleNewQuoteClick}
              >
                New Quote
              </button>
            )}

            {hasPerm("saleS_SAVED") && (
              <button
                className={location.pathname === "/savedquotes" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/savedquotes")}
              >
                Saved Quotes
              </button>
            )}

            {hasPerm("saleS_SUBMIT") && (
              <button
                className={location.pathname === "/approvalquotes" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/approvalquotes")}
              >
                Submitted For Approval
              </button>
            )}

            {hasPerm("saleS_ACTIVE") && (
              <button
                className={location.pathname === "/activequote" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/activequote")}
              >
                Active Quotes
              </button>
            )}

            {hasPerm("saleS_HISTORY") && (
              <button
                className={location.pathname === "/historicalquote" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/historicalquote")}
              >
                Historical Quotes
              </button>
            )}
          </>
        )}

        {showTravelerMenu && activeModule === "TRAVELER" && (
          <>
            {hasPerm("traveL_NEW") && (
              <button
                className={location.pathname === "/newquote" ? "tab active" : "tab"}
                onClick={handleNewQuoteClick}
              >
                New Quote
              </button>
            )}

            {hasPerm("traveL_SAVED") && (
              <button
                className={location.pathname === "/savedquotes" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/savedquotes")}
              >
                Saved Quotes
              </button>
            )}

            {hasPerm("traveL_SUBMIT") && (
              <button
                className={location.pathname === "/approvalquotes" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/approvalquotes")}
              >
                Submitted For Approval
              </button>
            )}

            {hasPerm("traveL_ACTIVE") && (
              <button
                className={location.pathname === "/activequote" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/activequote")}
              >
                Active Quotes
              </button>
            )}

            {hasPerm("traveL_HISTORY") && (
              <button
                className={location.pathname === "/historicalquote" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/historicalquote")}
              >
                Historical Quotes
              </button>
            )}
          </>
        )}

        {showBlackFlushMenu && activeModule === "BLACKFLUSH" && (
          <>
            {showBlackFlushRelease && (
              <button
                className={location.pathname === "/release-to-production" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/release-to-production")}
              >
                Release to Production
              </button>
            )}

            {showBlackFlushShipped && (
              <button
                className={location.pathname === "/shipped" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/shipped")}
              >
                Shipped
              </button>
            )}
          </>
        )}

        {showAdminMenu && activeModule === "ADMIN" && (
          <>
            {(hasPerm("useR_MANAGEMENT") || hasPerm("menU_ACCESS")) && (
              <button
                className={location.pathname === "/user-management" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/user-management")}
              >
                User Management
              </button>
            )}

            {hasPerm("partS_MANAGEMENT") && (
              <button
                className={location.pathname === "/parts-management" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/parts-management")}
              >
                Parts Management
              </button>
            )}

            {hasPerm("bundlE_APPROVAL") && (
              <button
                className={location.pathname === "/bundle-management" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/bundle-management")}
              >
                Bundle Management
              </button>
            )}

            {hasPerm("approvaL_WORKFLOW") && (
              <button
                className={location.pathname === "/approval-workflow" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/approval-workflow")}
              >
                Approval WorkFlow
              </button>
            )}

            {hasPerm("customers") && (
              <button
                className={location.pathname === "/customers" ? "tab active" : "tab"}
                onClick={() => handleNavigation("/customers")}
              >
                Customers
              </button>
            )}
          </>
        )}
      </div>
      </div>

      <div className="page-content">
        <Outlet key={pageKey} />
      </div>

      {showConfirm && (
        <ConfirmPopup
          message="Do you want to create a new quote?"
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      )}
    </div>
  );
};

export default Layout;
